<script setup lang="ts">
    import { ref, computed, toRef } from 'vue'

    import { boolCompare, isNullOrWhitespace, numCompare } from '@common/Common'

    import type {
        CriteriaVoteEntry,
        MeetingQuestionReviewReadModel
    } from '@Evaluation/CriteriaSurveys/Domain/readmodels'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        readModel: MeetingQuestionReviewReadModel
    }>()

    const emit = defineEmits<{
        (e: 'update-value', value: number | null): void
        (e: 'update-comment', comment: string): void
        (
            e: 'show-criterion-details',
            name: string,
            description: string,
            path: string | null
        ): void
    }>()

    // ---------------- shortcuts ----------------

    const rm = toRef(props, 'readModel')

    // ---------------- local state ----------------

    const browserActiveTab = ref<'votes' | 'summary' | 'comments'>('votes')

    const ScaleView1 = [
        9, 8, 7, 6, 5, 4, 3, 2, 1,
        1 / 2, 1 / 3, 1 / 4, 1 / 5, 1 / 6, 1 / 7, 1 / 8, 1 / 9
    ].sort((a, b) => numCompare(a, b))

    // ---------------- basic derived ----------------

    const QuestionId = computed(() => rm.value.Id)
    const ContextName = computed(() => rm.value.ContextCriterionName)
    const FirstItemTitle = computed(() => rm.value.FirstCriterionName)
    const SecondItemTitle = computed(() => rm.value.SecondCriterionName)

    const VotesEntries = computed<CriteriaVoteEntry[]>(() =>
        rm.value.VotesEntries
            ?.sort((a, b) => boolCompare(a.SurveyId != null, b.SurveyId != null))
        ?? []
    )

    const Value = computed<number | null>(() => rm.value.Value)
    const Comment = computed<string | null>(() => rm.value.Comment)

    // ---------------- selection state ----------------

    const EqualitySelected = computed(() => Value.value != null && Value.value === 1.0)
    const FirstSelected = computed(() => Value.value != null && Value.value > 1.0)
    const SecondSelected = computed(() => Value.value != null && Value.value < 1.0)

    const IntegerValue = computed<number | null>(() =>
    {
        if (!Value.value) return null
        const v = Value.value < 1 ? 1 / Value.value : Value.value
        return Math.round(v * 100) / 100
    })

    const FirstBtnLabel = computed(() =>
        FirstSelected.value && IntegerValue.value
            ? `${GetLabel(IntegerValue.value)} (${IntegerValue.value})`
            : 'More important'
    )

    const SecondBtnLabel = computed(() =>
        SecondSelected.value && IntegerValue.value
            ? `${GetLabel(IntegerValue.value)} (${IntegerValue.value})`
            : 'More important'
    )

    // ---------------- aggregation ----------------

    const Average = computed<number | null>(() =>
    {
        const entries = VotesEntries.value.filter(v => v.Value != null && v.VoteStrength > 0)
        if (entries.length < 1) return null

        const sum = entries.reduce((s, v) => s + v.VoteStrength, 0)

        let avg = 1.0
        for (const e of entries)
        {
            avg *= Math.pow(e.Value!, e.VoteStrength / sum)
        }

        return avg
    })

    const Comments = computed(() =>
        VotesEntries.value.filter(v => isNullOrWhitespace(v.Comment) === false)
    )

    const ScaleView = computed(() =>
    {
        const scale = [...ScaleView1]

        if (Average.value != null)
        {
            if (!scale.some(v => Math.round(v * 100) === Math.round(Average.value! * 100)))
            {
                scale.push(Average.value)
            }
        }

        return scale.sort((a, b) => numCompare(a, b)).reverse()
    })

    const LeftScaleValuesCount = computed(() =>
        ScaleView.value.filter(v => v > 1).length
    )

    const RightScaleValuesCount = computed(() =>
        ScaleView.value.filter(v => v < 1).length
    )

    const AverageDescription = computed(() =>
    {
        if (Average.value == null) return 'N/A'
        return Average.value < 1
            ? (1 / Average.value).toFixedIfNeeded(2)
            : Average.value.toFixedIfNeeded(2)
    })

    const HistogramValues = computed(() =>
    {
        const voteValues = VotesEntries.value.filter(v => v.Value != null).map(v => v.Value!)
        const uniqueValues = new Set([...ScaleView1, ...voteValues])
        const orderedValues = Array.from(uniqueValues).sort((a, b) => b - a)

        let maxCount = 0
        for (const value of orderedValues)
        {
            maxCount = Math.max(
                maxCount,
                voteValues.filter(v => v === value).length
            )
        }

        return orderedValues.map(value =>
        {
            const count = voteValues.filter(v => v === value).length
            return {
                Value: value,
                Count: count,
                PercentOfMax: Math.round(count / maxCount * 100)
            }
        })
    })

    // ---------------- actions ----------------

    function SaveValueCommand(value: number | null)
    {
        emit('update-value', value)
    }

    function SaveComment(comment: string)
    {
        emit('update-comment', comment)
    }

    function ShowContextDetailsCommand()
    {
        emit(
            'show-criterion-details',
            rm.value.ContextCriterionName,
            rm.value.ContextCriterionDescription,
            rm.value.ContextCriterionPath
        )
    }

    function ShowFirstItemDetailsCommand()
    {
        emit(
            'show-criterion-details',
            rm.value.FirstCriterionName,
            rm.value.FirstCriterionDescription,
            rm.value.FirstCriterionPath
        )
    }

    function ShowSecondItemDetailsCommand()
    {
        emit(
            'show-criterion-details',
            rm.value.SecondCriterionName,
            rm.value.SecondCriterionDescription,
            rm.value.SecondCriterionPath
        )
    }

    // ---------------- formatting helpers ----------------

    function FormatValue(value: number)
    {
        return value < 1 ? 1 / value : value
    }

    function GetColor(value: number)
    {
        const v = Math.round(value * 100) / 100
        if (v === 1) return 'bg-color-green2'
        if (v > 1) return 'bg-color-blue2'
        return 'bg-color-red2'
    }

    function GetCssClass(scaleValue: number, voteValue: number | null)
    {
        const type = GetScaleItemType(scaleValue, voteValue)

        if (type === 'Empty') return 'EmptyScaleItem'
        if (type === 'Hidden') return 'HiddenScaleItem'

        if (type === 'Filled' || type === 'DoubleFilled')
        {
            return `${type}ScaleItem ${GetColor(voteValue!)}`
        }

        return null
    }

    function GetScaleItemText(scaleValue: number, voteValue: number | null)
    {
        const type = GetScaleItemType(scaleValue, voteValue)
        return type !== 'DoubleFilled'
            ? FormatValue(scaleValue).toLocaleString()
            : FormatValue(voteValue!).toFixedIfNeeded(2)
    }

    function GetLabel(value: number): string
    {
        switch (value)
        {
            case 2: return 'Weakly'
            case 3: return 'Moderately'
            case 4: return 'Moderately +'
            case 5: return 'Strongly'
            case 6: return 'Strongly +'
            case 7: return 'Very Strongly'
            case 8: return 'Very Strongly +'
            case 9: return 'Extremely'
            default: return ''
        }
    }

    function GetScaleItemType(scaleValue: number, voteValue: number | null)
    {
        if (voteValue == null) return 'Empty'

        if (scaleValue < 1 && voteValue > 1) return 'Empty'
        if (scaleValue > 1 && voteValue < 1) return 'Empty'

        let x: number | null = null

        if (scaleValue <= 1 && voteValue <= 1)
            x = (1 / scaleValue) - (1 / voteValue)
        else if (scaleValue >= 1 && voteValue >= 1)
            x = scaleValue - voteValue

        x = Math.round(x! * 100) / 100

        if (x === 0) return 'Filled'
        if (x >= 1 || x <= -1) return 'Empty'
        if (x < 0) return 'Hidden'
        if (x > 0) return 'DoubleFilled'

        return null
    }

</script>


<template>
    <div v-if="rm">
        <p class="text-center lead" style="font-size: 1.5rem;">
            In the context of <a class="text-primary" @click="ShowContextDetailsCommand" title="Show details">{{ContextName}}</a>,
            which criterion is more important: <br />
            <a class="text-primary" @click="ShowFirstItemDetailsCommand" title="Show details">{{FirstItemTitle}}</a> or
            <a class="text-primary" @click="ShowSecondItemDetailsCommand" title="Show details">{{SecondItemTitle}}</a>?
        </p>

        <div class="card card-collapsable mt-1">
            <div class="card-header pointer" data-toggle="collapse" href="#cardQuestion">
                Group vote
            </div>
            <div id="cardQuestion" class="card-body collapse show pt-4">
                <div style="min-height: 450px;">
                    <div class="PairwiseComparisonEditor">

                        <div class="row">
                            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12">
                                <div class="mt1 ItemDetailsBox bg-color-blue3">
                                    <h4>{{FirstItemTitle}}</h4>
                                </div>
                                <div class="btn-group ItemButton">
                                    <button type="button" class="btn btn-block dropdown-toggle" data-toggle="dropdown" :class="FirstSelected ? 'btn-success' : 'btn-outline-secondary'">
                                        <i class="fa-regular" aria-hidden="true" :class="FirstSelected ? 'fa-square-check' : 'fa-square'"></i>
                                        {{FirstBtnLabel}}
                                    </button>
                                    <div class="dropdown-menu">
                                        <h4 class="dropdown-header">How much more important?</h4>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item" @click="SaveValueCommand(2)">Weak (2)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(3)">Moderate (3)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(4)">Moderate + (4)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(5)">Strong (5)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(6)">Strong + (6)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(7)">Very strong (7)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(8)">Very strong + (8)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(9)">Extreme (9)</a>
                                    </div>
                                </div>
                            </div>

                            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12">
                                <div class="mt1 ItemDetailsBox bg-color-red3">
                                    <h4>{{SecondItemTitle}}</h4>
                                </div>
                                <div class="btn-group ItemButton">
                                    <button type="button" class="btn btn-block dropdown-toggle" data-toggle="dropdown" :class="SecondSelected ? 'btn-success' : 'btn-outline-secondary'">
                                        <i class="fa-regular" aria-hidden="true" :class="SecondSelected ? 'fa-square-check' : 'fa-square'"></i> {{SecondBtnLabel}}
                                    </button>
                                    <div class="dropdown-menu">
                                        <h4 class="dropdown-header">How much more important?</h4>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/2.0)">Weak (2)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/3.0)">Moderate (3)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/4.0)">Moderate + (4)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/5.0)">Strong (5)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/6.0)">Strong + (6)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/7.0)">Very strong (7)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/8.0)">Very strong + (8)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/9.0)">Extreme (9)</a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-lg-6 offset-lg-3 col-md-6 offset-md-3 col-sm-6 offset-sm-3 col-xs-12">
                                <button type="button" class="btn btn-block btn-equal mt1" @click="SaveValueCommand(1.0)" :class="EqualitySelected === true ? 'btn-success' : 'btn-outline-secondary'">
                                    <i class="fa-regular" aria-hidden="true" :class="EqualitySelected === true ? 'fa-square-check' : 'fa-square'"></i>
                                    Equally important (1)
                                </button>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-lg-6 offset-lg-3 col-md-6 offset-md-3 col-sm-6 offset-sm-3 col-xs-12">
                                <button type="button" class="btn btn-block mt1" @click="SaveValueCommand(null)"
                                        :class="Value == null ? 'btn-success' : 'btn-outline-secondary'">
                                    <i class="fa-regular" aria-hidden="true" :class="Value == null ? 'fa-square-check' : 'fa-square'"></i>
                                    Follow the average (default)
                                </button>
                            </div>
                        </div>

                        <div style="margin-top: 30px;">
                            <h5 style="margin-bottom: 5px;">Comment:</h5>
                            <textarea rows="5" :value.trim="Comment" @input="SaveComment($event.target.value)" class="form-control" placeholder="Comment..."></textarea>
                        </div>
                    </div>

                </div>
            </div>
        </div>


        <div class="mt-3">
            <div class="mt-4" style="min-height: 500px;">
                <div class="PairwiseComparisonVotesBrowserView">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" @click="browserActiveTab = 'votes'" :class="{'active': browserActiveTab === 'votes'}">Votes</a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link" @click="browserActiveTab = 'summary'" :class="{'active': browserActiveTab === 'summary'}">Chart</a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link" @click="browserActiveTab = 'comments'" :class="{'active': browserActiveTab === 'comments'}">Comments</a>
                        </li>
                    </ul>

                    <div class="tab-content">
                        <div v-if="browserActiveTab === 'votes'" class="tab-pane pt-2" :class="{'active': browserActiveTab === 'votes'}">

                            <div class="table-responsive">
                                <table class="table table-striped">
                                    <thead>
                                        <tr>
                                            <th style="border-top: 0;"></th>
                                            <th class="clearfix" style="min-width: 510px; border-top: 0;">
                                                <div class="" style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
                                                    <div class="pull-left" style="flex: 1; text-align: left;">{{FirstItemTitle}}</div>
                                                    <div class="text-center" style="flex: 1; text-align: center;">vs.</div>
                                                    <div class="pull-right" style="flex: 1; text-align: right;">{{SecondItemTitle}}</div>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr style="text-align: center;">
                                            <td style="text-align: left;">
                                                <strong>Average ({{AverageDescription}})</strong>
                                            </td>
                                            <td style="padding: 0;">
                                                <ul class="clearfix" style="list-style: none; margin: 0; padding: 0;">
                                                    <li v-for="scaleValue in ScaleView1" :class="GetCssClass(scaleValue, Average)">
                                                        {{GetScaleItemText(scaleValue, Average)}}
                                                    </li>
                                                </ul>
                                            </td>
                                        </tr>

                                        <tr style="text-align: center;" v-for="voteEntry in VotesEntries">
                                            <td style="text-align: left;">
                                                {{voteEntry.VotesSourceName ?? '???'}}
                                            </td>
                                            <td style="padding: 0;">
                                                <ul class="clearfix" style="list-style: none; margin: 0; padding: 0;">
                                                    <li v-for="scaleValue in ScaleView1" :class="GetCssClass(scaleValue, voteEntry.Value)">
                                                        {{GetScaleItemText(scaleValue, voteEntry.Value)}}
                                                    </li>
                                                </ul>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                        </div>

                        <div v-if="browserActiveTab === 'summary'" class="tab-pane pt-2" :class="{'active': browserActiveTab === 'summary'}">

                            <div class="table-responsive">
                                <table class="table">
                                    <tbody>
                                        <tr style="text-align: center;" class="bottom noborder">
                                            <td v-for="v in HistogramValues" style="height: 400px; border: none; padding-top: 20px;">
                                                <div v-if="v.Count" v-html="v.Count"></div>
                                                <div :style="{'height': `${v.PercentOfMax}%`}" :class="GetColor(v.Value)" class="bg-color-blue2">&nbsp;</div>
                                            </td>
                                        </tr>
                                        <tr style="text-align: center;">
                                            <td v-for="v in HistogramValues" style="width: 6%;">
                                                {{FormatValue(v.Value).toFixedIfNeeded(2)}}
                                            </td>
                                        </tr>
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <th :colspan="LeftScaleValuesCount" style="text-align: left;">
                                                {{FirstItemTitle}}
                                            </th>
                                            <th style="text-align: center;">vs</th>
                                            <th :colspan="RightScaleValuesCount" style="text-align: right;">
                                                {{SecondItemTitle}}
                                            </th>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                        </div>

                        <div v-if="browserActiveTab === 'comments'" class="tab-pane pt-2" :class="{'active': browserActiveTab === 'comments'}">
                            <blockquote class="blockquote" v-for="c in Comments">
                                <p class="mb-0">{{c.Comment}}</p>
                                <footer class="blockquote-footer">{{c.VotesSourceName ?? '???'}}</footer>
                            </blockquote>
                        </div>
                    </div>
                </div>

            </div>
        </div>

    </div>
</template>

<style scoped>
    .PairwiseComparisonEditor .btn, .PairwiseComparisonEditor textarea {
        border-radius: 0;
    }

    .PairwiseComparisonEditor .mt1 {
        margin-top: 20px !important;
    }

    .PairwiseComparisonEditor .mt2 {
        margin-top: 40px !important;
    }

    .PairwiseComparisonEditor div.ItemDetailsBox {
        border: 1px solid #ddd;
        border-bottom: 0;
        height: 90px;
        max-height: 90px;
        overflow-y: auto;
        padding: 15px 10px;
        background-color: #f5f5f5;
    }

        .PairwiseComparisonEditor div.ItemDetailsBox h4 {
            padding: 0;
            margin: 0;
            text-align: center;
        }

    .PairwiseComparisonEditor div.ItemButton {
        width: 100%;
        padding: 0px;
    }

        .PairwiseComparisonEditor div.ItemButton a.btn {
            height: 38px;
            padding-top: 8px;
        }

        .PairwiseComparisonEditor div.ItemButton div.dropdown-menu {
            width: 100%;
        }

    .PairwiseComparisonEditor a.btn-equal {
        height: 38px;
        padding-top: 8px;
        width: 100%;
    }

    .PairwiseComparisonEditor a.btn-geometric-mean {
        height: 38px;
        padding-top: 8px;
        width: 100%;
    }



    .PairwiseComparisonVotesBrowserView tr.bottom td {
        vertical-align: bottom !important;
    }


    .PairwiseComparisonVotesBrowserView ul li.EmptyScaleItem {
        display: block;
        float: right;
        min-width: 30px;
        width: 5%;
        padding: 8px;
    }

    .PairwiseComparisonVotesBrowserView ul li.FilledScaleItem {
        display: block;
        float: right;
        min-width: 30px;
        width: 5%;
        padding: 8px;
        color: white;
    }

    .PairwiseComparisonVotesBrowserView ul li.HiddenScaleItem {
        display: none;
    }

    .PairwiseComparisonVotesBrowserView ul li.DoubleFilledScaleItem {
        display: block;
        float: right;
        min-width: 60px;
        width: 10%;
        padding: 8px;
        color: white;
    }
</style>